import Loading from "@components/loading/index.jsx";
import { MailClosedIcon, MailOpenIcon } from "@layouts/betto/shared/NotificationMailIcons.jsx";
import { BettoEmptyState } from "@pages/profile-v2/betto/components/BettoEmptyState";
import { isNotificationUnread } from "@utils/notifications";
import moment from "moment";
import PropTypes from "prop-types";
import React, { memo } from "react";
import { getTranslate } from "react-localize-redux";
import { connect } from "react-redux";

import {
	BettoCheckboxWrapper,
	BettoCustomCheckbox,
	BettoInboxBulkButton,
	BettoInboxCard,
	BettoInboxCardActions,
	BettoInboxCardContent,
	BettoInboxCardTitle,
	BettoInboxContentCard,
	BettoInboxControlActions,
	BettoInboxControlBar,
	BettoInboxFooter,
	BettoInboxList,
	BettoInboxLoadingRef,
	BettoInboxMailButton,
	BettoInboxMessage,
	BettoInboxMetaGroup,
	BettoInboxMetaItem,
	BettoInboxPreview,
	BettoInboxRoot,
	BettoInboxSelectedCount,
	BettoInboxTitle,
	BettoInboxTitleGroup,
	BettoInboxTitleRow,
	BettoInboxUnreadDot,
} from "./elements";
import { useInbox } from "./useInbox";

const CalendarIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 14 14"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true">
		<rect
			x="1.75"
			y="2.625"
			width="10.5"
			height="9.625"
			rx="1.5"
			stroke="currentColor"
			strokeWidth="1.1"
		/>
		<path
			d="M1.75 5.833H12.25M4.667 1.75V3.5M9.333 1.75V3.5"
			stroke="currentColor"
			strokeWidth="1.1"
			strokeLinecap="round"
		/>
	</svg>
);

const ClockIcon = () => (
	<svg
		width="14"
		height="14"
		viewBox="0 0 14 14"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true">
		<circle
			cx="7"
			cy="7"
			r="5.25"
			stroke="currentColor"
			strokeWidth="1.1"
		/>
		<path
			d="M7 4.667V7L8.75 8.75"
			stroke="currentColor"
			strokeWidth="1.1"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const TrashIcon = () => (
	<svg
		width="18"
		height="18"
		viewBox="0 0 14 14"
		fill="none"
		xmlns="http://www.w3.org/2000/svg"
		aria-hidden="true">
		<path
			d="M2.625 4.667H11.375M5.833 4.667V3.208C5.833 2.763 6.196 2.4 6.642 2.4H7.358C7.804 2.4 8.167 2.763 8.167 3.208V4.667M5.542 6.708V10.208M8.458 6.708V10.208M4.375 4.667L4.958 11.192C4.958 11.638 5.321 12 5.767 12H8.233C8.679 12 9.042 11.638 9.042 11.192L9.625 4.667"
			stroke="currentColor"
			strokeWidth="1.1"
			strokeLinecap="round"
			strokeLinejoin="round"
		/>
	</svg>
);

const stripHtml = (html) => {
	if (!html) {
		return "";
	}

	return String(html)
		.replace(/<[^>]*>/g, " ")
		.replace(/\s+/g, " ")
		.trim();
};

export const Inbox = memo(({ translate, location }) => {
	const {
		notifications,
		loading,
		loadingRef,
		activeItemIndex,
		allRead,
		selectedIds,
		handleCardClick,
		handleToggleRead,
		handleDelete,
		handleDeleteAll,
		markAllAsRead,
		handleToggleSelect,
		handleSelectAll,
		handleMarkSelectedAsRead,
		handleDeleteSelected,
		normalizeMessage,
	} = useInbox(location);

	const isAllSelected =
		notifications.length > 0 &&
		notifications.every((item) => selectedIds.has(item.submissionTime));

	return (
		<BettoInboxRoot>
			<BettoInboxContentCard>
				<BettoInboxTitle>{translate("INBOX")}</BettoInboxTitle>

				<Loading loading={loading} />

				{notifications.length > 0 ? (
					<BettoInboxControlBar>
						<BettoInboxControlActions>
							{selectedIds.size > 0 ? (
								<>
									<BettoInboxSelectedCount>
										{translate("SELECTED_COUNT", { count: selectedIds.size })}
									</BettoInboxSelectedCount>
									<BettoInboxBulkButton
										type="button"
										onClick={handleMarkSelectedAsRead}>
										{translate("MARK AS READ")}
									</BettoInboxBulkButton>
									<BettoInboxBulkButton
										type="button"
										onClick={handleDeleteSelected}>
										{translate("betto.notifications.delete")}
									</BettoInboxBulkButton>
								</>
							) : (
								<>
									{!allRead ? (
										<BettoInboxBulkButton
											type="button"
											onClick={markAllAsRead}>
											{translate("MARK ALL AS READ")}
										</BettoInboxBulkButton>
									) : null}
									<BettoInboxBulkButton
										type="button"
										onClick={handleDeleteAll}>
										{translate("DELETE ALL")}
									</BettoInboxBulkButton>
								</>
							)}
						</BettoInboxControlActions>
						<BettoCheckboxWrapper
							onClick={(event) => event.stopPropagation()}
							aria-label={translate("SELECT_ALL")}>
							<input
								type="checkbox"
								style={{ display: "none" }}
								checked={isAllSelected}
								onChange={handleSelectAll}
							/>
							<BettoCustomCheckbox $checked={isAllSelected} />
						</BettoCheckboxWrapper>
					</BettoInboxControlBar>
				) : null}

				{notifications.length > 0 ? (
					<BettoInboxList>
						{notifications.map((item, index) => {
							const timestamp = parseInt(item?.time ?? item?.submissionTime, 10);
							const isUnread = isNotificationUnread(item);
							const isExpanded = activeItemIndex === index;
							const preview = stripHtml(item.message);
							const isSelected = selectedIds.has(item.submissionTime);

							return (
								<BettoInboxCard
									key={`betto-inbox-${item.submissionTime}-${index}`}
									$isUnread={isUnread}
									onClick={() => handleCardClick(item, index)}>
									<BettoInboxCardContent>
										<BettoInboxTitleRow>
											<BettoInboxTitleGroup>
												{isUnread ? (
													<BettoInboxUnreadDot aria-hidden="true" />
												) : null}
												<BettoInboxCardTitle>
													{translate(item.subject)}
												</BettoInboxCardTitle>
											</BettoInboxTitleGroup>
											<BettoCheckboxWrapper
												onClick={(event) => event.stopPropagation()}>
												<input
													type="checkbox"
													style={{ display: "none" }}
													checked={isSelected}
													onChange={(event) =>
														handleToggleSelect(
															event,
															item.submissionTime
														)
													}
												/>
												<BettoCustomCheckbox $checked={isSelected} />
											</BettoCheckboxWrapper>
										</BettoInboxTitleRow>
										{!isExpanded && preview ? (
											<BettoInboxPreview>{preview}</BettoInboxPreview>
										) : null}
									</BettoInboxCardContent>

									{isExpanded ? (
										<BettoInboxMessage
											onClick={(event) => event.stopPropagation()}
											dangerouslySetInnerHTML={{
												__html: normalizeMessage(item.message),
											}}
										/>
									) : null}

									<BettoInboxFooter onClick={(event) => event.stopPropagation()}>
										<BettoInboxMetaGroup>
											<BettoInboxMetaItem>
												<CalendarIcon />
												{moment(timestamp).format("M/D/YYYY")}
											</BettoInboxMetaItem>
											<BettoInboxMetaItem>
												<ClockIcon />
												{moment(timestamp).format("h:mm:ss A")}
											</BettoInboxMetaItem>
										</BettoInboxMetaGroup>

										<BettoInboxCardActions>
											<BettoInboxMailButton
												type="button"
												$isUnread={isUnread}
												aria-label={
													isUnread
														? translate("MARK AS READ")
														: translate("MARK AS UNREAD")
												}
												onClick={(event) =>
													handleToggleRead(event, item, index)
												}>
												{isUnread ? (
													<MailClosedIcon size={18} />
												) : (
													<MailOpenIcon size={18} />
												)}
											</BettoInboxMailButton>
											<BettoInboxMailButton
												type="button"
												aria-label={translate("betto.notifications.delete")}
												onClick={(event) =>
													handleDelete(event, item, index)
												}>
												<TrashIcon />
											</BettoInboxMailButton>
										</BettoInboxCardActions>
									</BettoInboxFooter>
								</BettoInboxCard>
							);
						})}

						<BettoInboxLoadingRef innerRef={loadingRef}>
							{loading ? translate("LOADING") : null}
						</BettoInboxLoadingRef>
					</BettoInboxList>
				) : (
					!loading && <BettoEmptyState title={translate("NoInfoInbox")} />
				)}
			</BettoInboxContentCard>
		</BettoInboxRoot>
	);
});

Inbox.displayName = "Inbox";

Inbox.propTypes = {
	translate: PropTypes.func.isRequired,
	location: PropTypes.shape({
		search: PropTypes.string,
	}).isRequired,
};

const mapStateToProps = (state) => ({
	translate: getTranslate(state.localize),
});

export default connect(mapStateToProps)(Inbox);
